import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  ReProductionFormData,
  ToastType,
  ProductionStatus,
} from '../ReProductionUtils';
import {useDispatch} from 'react-redux';
import {setReproductionIsFieldsChanged} from '../../../../redux/slice/reproductionSlice';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import './ReProductionNumbering.scss';
import {
  useReProductionNumbering,
  useSaveReProductionNumbering,
} from '../../../../hooks/productions/useReProductions';

const SUB_BATES_MIN_NUMBER_LENGTH_DEFAULT = 5;

const resolveSubBatesMinNumberLength = (
  value: number | undefined
): number => {
  if (value === undefined || value < 1) {
    return SUB_BATES_MIN_NUMBER_LENGTH_DEFAULT;
  }
  return value;
};

const isSubBatesMinNumberLengthInvalid = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) || parsed < 1;
};

interface ReProductionNumberingProps {
  reProductionId: number;
  securityGroupId: number;
  formData: ReProductionFormData;
  onFormDataChange: (updates: Partial<ReProductionFormData>) => void;
  getStatus: (isShowToast: boolean, status: ToastType, message: string) => void;
  goNext: () => void;
  goBack: () => void;
  onStateChange: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  productionStatusCode?: string;
}

const ReProductionNumbering = forwardRef<
  {save: () => void; next: () => void},
  ReProductionNumberingProps
>(
  (
    {
      securityGroupId,
      reProductionId,
      formData,
      onFormDataChange,
      goNext,
      onStateChange,
      getStatus,
      productionStatusCode,
    },
    ref
  ) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Sub-bates min number length field is always shown for re-production.
    const showSubBates = true;
    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode === ProductionStatus.INPROGRESS;
      
    const [minNumberLength, setMinNumberLength] = useState<string>('');
    const [minNumberLengthError, setMinNumberLengthError] =
      useState<string>('');

    useEffect(() => {
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
    }, [dispatch]);

    //GET API
    const {data, isLoading} = useReProductionNumbering(
      {
        reProductionId: reProductionId,
        securityGrpId: securityGroupId,
      },
            showSubBates
    );

    // Populate UI from formData (step navigation) or API (0 maps to default 5)
    useEffect(() => {
      if (!showSubBates) return;

      if (formData?.numbering?.minNumberLength !== undefined) {
        setMinNumberLength(
          resolveSubBatesMinNumberLength(
            formData.numbering.minNumberLength
          ).toString()
        );
        return;
      }

      if (data?.minNumberLength !== undefined) {
        setMinNumberLength(
          resolveSubBatesMinNumberLength(data.minNumberLength).toString()
        );
        return;
      }

      setMinNumberLength(SUB_BATES_MIN_NUMBER_LENGTH_DEFAULT.toString());
    }, [showSubBates, data, formData]);

    //SAVE API
    const {mutate: saveNumbering, isPending: isSaving} =
      useSaveReProductionNumbering(
        (res) => {
          const apiMessage = (res?.message || '').trim().toLowerCase();
          const successMessage =
            apiMessage === 'mark complete successful'
              ? t('reProduction.toastMessages.nextSuccess')
              : t('reProduction.toastMessages.saveSuccess');
          getStatus(true, ToastType.Success, successMessage);
        },
        (err) => {
          getStatus(true, ToastType.Error, err.message);
        }
      );

    const validateMinNumberLength = (): boolean => {
      if (!showSubBates) {
        setMinNumberLengthError('');
        return true;
      }

      const trimmed = minNumberLength.trim();
      if (!trimmed) {
        setMinNumberLengthError(
          t('reProduction.validation.minNumberLengthRequired')
        );
        return false;
      }

      if (Number(trimmed) < 1) {
        setMinNumberLengthError(
          t('reProduction.validation.minNumberLengthMinValue')
        );
        return false;
      }

      setMinNumberLengthError('');
      return true;
    };

    useEffect(() => {
      if (showSubBates) {
        const isInvalid = isSubBatesMinNumberLengthInvalid(minNumberLength);
        onStateChange({
          saveDisabled:
            isInProgressOrCompleted || isInvalid || isSaving,
          nextDisabled:
            (isInvalid || isSaving) && !isInProgressOrCompleted,
        });
        return;
      }

      onStateChange({
        saveDisabled: isInProgressOrCompleted || isSaving,
        nextDisabled: isSaving && !isInProgressOrCompleted,
      });
    }, [
      minNumberLength,
      onStateChange,
      showSubBates,
      isSaving,
      isInProgressOrCompleted,
    ]);

    useImperativeHandle(ref, () => ({
      save: () => {
        if (isInProgressOrCompleted) return;
        if (!validateMinNumberLength()) {
          return;
        }
        const payload = {
          reProductionId: reProductionId,
          securityGrpId: securityGroupId,
          minimumNoLength: Number(minNumberLength),
          IsMarkComplete: false,
        };

        saveNumbering(payload);

        onFormDataChange({
          numbering: {
            minNumberLength: minNumberLength
              ? Number(minNumberLength)
              : undefined,
          },
        });
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      },
      next: () => {
        if (isInProgressOrCompleted) {
          goNext();
          return;
        }
        if (!validateMinNumberLength()) {
          return;
        }
        const payload = {
          reProductionId: reProductionId,
          securityGrpId: securityGroupId,
          minimumNoLength: Number(minNumberLength),
          IsMarkComplete: true,
        };

        saveNumbering(payload);

        onFormDataChange({
          numbering: {
            minNumberLength: minNumberLength
              ? Number(minNumberLength)
              : undefined,
          },
        });
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
        goNext();
      },
    }));

    const handleMinNumberLengthChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (isInProgressOrCompleted) return;
      const value = e.target.value.replace(/\D/g, '');
      setMinNumberLength(value);
      if (!showSubBates) {
        return;
      }
      if (!value.trim()) {
        setMinNumberLengthError(
          t('reProduction.validation.minNumberLengthRequired')
        );
      } else if (Number(value) < 1) {
        setMinNumberLengthError(
          t('reProduction.validation.minNumberLengthMinValue')
        );
      } else {
        setMinNumberLengthError('');
      }
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: true}));
    };

    if (isLoading) {
      return <div>Loading...</div>;
    }

    const fieldState = isInProgressOrCompleted
      ? 'inactive'
      : minNumberLengthError
        ? 'error'
        : 'enabled';

    return (
      <Box
        className={`re-production-numbering${
          isInProgressOrCompleted ? ' re-production-numbering--readonly' : ''
        }`}
      >
        <Box className="re-production-numbering__header">
          <Typography className="re-production-numbering__header-title">
            {t('reProduction.numbering.sectionTitle')}
          </Typography>
        </Box>

        <Box className="re-production-numbering__body">
          <Box className="re-production-numbering__info-banner">
            <InfoIcon className="re-production-numbering__info-banner-icon" />
            <Typography className="re-production-numbering__info-banner-text">
              {t('reProduction.numbering.infoBanner')}
            </Typography>
          </Box>

          {showSubBates && (
            <Box className="re-production-numbering__sub-bates">
              <Box className="re-production-numbering__sub-bates-title-row">
                <Typography className="re-production-numbering__sub-bates-title typo-text subhead4_size">
                  {t('reProduction.numbering.subBatesTitle')}
                </Typography>
                <ArrTooltipWrapper
                  title={t('reProduction.numbering.subBatesTitle')}
                  trigger={<InfoIcon className="info-icon" />}
                >
                  {t('reProduction.numbering.subBatesTooltip')}
                </ArrTooltipWrapper>
              </Box>

              <Box className="re-production-numbering__sub-bates-field">
                <Typography className="re-production-numbering__sub-bates-field-label typo-text body3_size">
                  {`${t('reProduction.numbering.minNumberLength')} *`}
                </Typography>
                <ArrTextField
                  id="re-production-min-number-length"
                  dataTestId="re-production-min-number-length"
                  value={minNumberLength}
                  onChange={handleMinNumberLengthChange}
                  state={fieldState}
                  supportText={minNumberLengthError}
                  showErrorIcon={Boolean(minNumberLengthError)}
                  sizeVariant="medium"
                  slotProps={{
                    input: {
                      'aria-label': t('reProduction.numbering.minNumberLength'),
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

ReProductionNumbering.displayName = 'ReProductionNumbering';
export default ReProductionNumbering;
